import { Badge, Button, Link } from '@elsa/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useOrg } from '../lib/session.tsx';
import { app, must } from '../lib/supabase.ts';

// Cautarile salvate (app.saved_searches, RLS: doar ale tale, in firma curenta): filtrele unei
// cautari, ca sa o redeschizi dintr-un clic.

export interface SavedSearch {
  id: string;
  name: string;
  query: { kind?: 'acq' | 'item'; params?: Record<string, unknown> };
  created_at: string;
}

export function savedUrl(s: SavedSearch): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(s.query.params ?? {})) {
    if (v !== undefined && v !== null && v !== '' && typeof v !== 'object') q.set(k, String(v));
  }
  const path = s.query.kind === 'item' ? '/app/produse' : '/app/achizitii';
  return `${path}${q.toString() ? `?${q}` : ''}`;
}

export function useSavedSearches() {
  const { org, session } = useOrg();
  return useQuery({
    queryKey: ['saved_searches', org.id],
    queryFn: () =>
      must(
        app()
          .from('saved_searches')
          .select('id, name, query, created_at')
          .eq('organization_id', org.id)
          .eq('user_id', session.user.id)
          .order('created_at', { ascending: false }),
      ) as Promise<SavedSearch[]>,
  });
}

export function SaveSearchButton({
  kind,
  params,
}: {
  kind: 'acq' | 'item';
  params: Record<string, unknown>;
}) {
  const { org, session } = useOrg();
  const qc = useQueryClient();
  const save = useMutation({
    mutationFn: (name: string) =>
      must(
        app().from('saved_searches').insert({
          organization_id: org.id,
          user_id: session.user.id,
          name,
          query: { kind, params },
        }),
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['saved_searches'] }),
  });
  if (!Object.keys(params).length) return null;
  return (
    <Button
      disabled={save.isPending}
      onClick={() => {
        const name = prompt(
          'Numele căutării:',
          typeof params.q === 'string' ? params.q : '',
        )?.trim();
        if (name) save.mutate(name.slice(0, 120));
      }}
    >
      {save.isSuccess ? 'Salvată' : 'Salvează căutarea'}
    </Button>
  );
}

export function SavedSearchList({ kind }: { kind?: 'acq' | 'item' }) {
  const qc = useQueryClient();
  const q = useSavedSearches();
  const remove = useMutation({
    mutationFn: (id: string) => must(app().from('saved_searches').delete().eq('id', id)),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['saved_searches'] }),
  });
  const rows = (q.data ?? []).filter((s) => !kind || (s.query.kind ?? 'acq') === kind);
  if (!rows.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-2 text-sm">
      <span className="text-xs text-gri-700">Căutări salvate:</span>
      {rows.map((s) => (
        <span
          key={s.id}
          className="inline-flex items-center gap-1 border-2 border-cerneala bg-hartie py-0.5 pr-1 pl-2.5 text-xs font-semibold"
        >
          <Link to={savedUrl(s)} className="hover:underline">
            {s.name}
          </Link>
          {!kind && s.query.kind === 'item' ? <Badge>produse</Badge> : null}
          <button
            type="button"
            aria-label={`Șterge căutarea ${s.name}`}
            className="px-1.5 text-gri-600 hover:bg-gri-200 hover:text-cerneala"
            onClick={() => remove.mutate(s.id)}
          >
            ×
          </button>
        </span>
      ))}
    </div>
  );
}
